import { useState, useEffect } from "react";
import { Checkbox, Input, Button } from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import FloatLabel from "./FloatLabel";
import "./ChecklistComponent.css";

const ChecklistComponent = ({
  checklist = [],
  setChecklist,
  deletedChecklistIds,
  setDeletedChecklistIds,
}) => {
  const [newChecklistText, setNewChecklistText] = useState("");
  const [editingItemId, setEditingItemId] = useState(null);
  const [editChecklistText, setEditChecklistText] = useState("");
  const [isAdding, setIsAdding] = useState(false);
  const [formattedChecklist, setFormattedChecklist] = useState([]);
  const [deleteChecklistId, setDeleteChecklistId] = useState([]);
  // Sync checklist with formattedChecklist when checklist updates
  useEffect(() => {
    setFormattedChecklist(
      checklist.map((item) => ({
        ...item,
        id: item.id ?? item.checklist_id ?? item.event_record_id, // always have id
        checked: item.is_checked === 1,
      }))
    );
  }, [checklist]);

  const handleCheckboxChange = (id) => {
    setChecklist((prevChecklist) =>
      prevChecklist.map((item) =>
        (item.id ?? item.checklist_id ?? item.event_record_id) === id
          ? { ...item, is_checked: item.is_checked === 1 ? 0 : 1 }
          : item
      )
    );
  };

  const handleEditChecklist = (item) => {
    setEditingItemId(item.id);
    setEditChecklistText(item.title);
  };

  const handleSaveEditChecklist = (id) => {
    if (!editChecklistText.trim()) return;

    setFormattedChecklist((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, title: editChecklistText } : item
      )
    );

    setChecklist((prev) =>
      prev.map((item) =>
        (item.id ?? item.checklist_id ?? item.event_record_id) === id
          ? { ...item, title: editChecklistText }
          : item
      )
    );

    setEditingItemId(null);
    setEditChecklistText("");
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditChecklistText("");
  };
  const handleDeleteChecklist = (id) => {
    id = Number(id);
    setDeleteChecklistId((prev) => {
      const newState = prev.includes(id) ? prev : [...prev, id];
      setDeletedChecklistIds && setDeletedChecklistIds(newState);
      return newState;
    });

    setFormattedChecklist((prev) =>
      prev.filter((item) => Number(item.id) !== id)
    );
    setChecklist((prev) =>
      prev.filter(
        (item) =>
          Number(item.id ?? item.checklist_id ?? item.event_record_id) !== id
      )
    );
  };

  const handleAddChecklist = () => {
    if (!isAdding) {
      setIsAdding(true);
      return;
    }
    if (!newChecklistText.trim()) return;

    const newId = Date.now();
    const newItem = {
      id: newId,
      checklist_id: null,
      title: newChecklistText,
      is_checked: 0,
      checked: false,
      new_status: true,
    };

    setFormattedChecklist((prev) => [...prev, newItem]);
    setChecklist((prev) => [...prev, newItem]);
    setNewChecklistText("");
    setIsAdding(false);
  };

  return (
    <div className="checklist-container">
      <div className="checklist-header">
        <h3>Checklists</h3>
      </div>

      {formattedChecklist.map((item) => (
        <div key={item.id} className="checklist-item">
          <Checkbox
            className="checlistCheckbox"
            checked={item.is_checked === 1}
            onChange={() => handleCheckboxChange(item.id)}
            style={{
              color: item.is_checked === 1 || item.checked ? "#9A4FE0" : "#000",
            }}
          >
            {editingItemId === item.id ? (
              <FloatLabel label="Edit Checklist" value={editChecklistText}>
                <div style={{ display: "flex", alignItems: "center" }}>
                  <Input
                    value={editChecklistText}
                    onChange={(e) => setEditChecklistText(e.target.value)}
                    autoFocus
                    onPressEnter={() => handleSaveEditChecklist(item.id)}
                    onKeyDown={(e) => e.key === "Escape" && handleCancelEdit()}
                    style={{ marginLeft: "10px" }}
                  />
                  <button
                    className="confirnIcon"
                    style={{ marginLeft: "10px" }}
                    onClick={() => handleSaveEditChecklist(item.id)}
                    tabIndex={-1}
                  >
                    ✓
                  </button>
                  <button
                    className="confirnIcon"
                    style={{ marginLeft: "5px" }}
                    onClick={handleCancelEdit}
                    tabIndex={-1}
                  >
                    ✕
                  </button>
                </div>
              </FloatLabel>
            ) : (
              item.title
            )}
          </Checkbox>
          {editingItemId !== item.id && (
            <div className="checklist-actions">
              <Button
                className="leftChecklistAction"
                type="text"
                icon={<EditOutlined />}
                onClick={() => handleEditChecklist(item)}
              />
              <Button
                className="rightChecklistAction"
                type="text"
                icon={<DeleteOutlined />}
                onClick={() => handleDeleteChecklist(item.id)}
              />
            </div>
          )}
        </div>
      ))}

      {isAdding && (
        <div className="add-checklist">
          <FloatLabel label="Add Checklist">
            <div style={{ display: "flex", alignItems: "center" }}>
              <Input
                placeholder="Enter checklist item"
                value={newChecklistText}
                onChange={(e) => setNewChecklistText(e.target.value)}
                onPressEnter={handleAddChecklist}
                autoFocus
                style={{ marginLeft: "10px" }}
              />
              <button
                className="confirnIcon"
                style={{ marginLeft: "10px" }}
                onClick={handleAddChecklist}
                tabIndex={-1}
              >
                ✓
              </button>
              <button
                className="confirnIcon"
                style={{ marginLeft: "5px" }}
                onClick={() => {
                  setIsAdding(false);
                  setNewChecklistText("");
                }}
                tabIndex={-1}
              >
                ✕
              </button>
            </div>
          </FloatLabel>
        </div>
      )}

      <div className="addChecKlistBtn">
        <span style={{ marginRight: "10px" }}>Add Checklist</span>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAddChecklist}
          style={{ borderRadius: "100%" }}
        />
      </div>
    </div>
  );
};

export default ChecklistComponent;
